import { Injectable } from '@angular/core';

/**
 * Represents a software installation configuration pair from master data
 */
export interface SoftwareInstallationPair {
  id: string;
  name: string;
  img?: string;
  installations?: {
    [osType: string]: {
      base?: Record<string, unknown>;
      [key: string]: any;
    } | undefined;
  };
}

/**
 * Service for filtering software options based on OS type and version
 */
@Injectable({
  providedIn: 'root'
})
export class SoftwareFilterService {

  private debugMode = false;

  constructor() { }

  private log(message: string, data?: any): void {
    if (this.debugMode) {
      if (data) {
        console.log(`[SoftwareFilter] ${message}`, data);
      } else {
        console.log(`[SoftwareFilter] ${message}`);
      }
    }
  }

  /**
   * Determines if a software package is compatible with the given OS type and version.
   */
  isSoftwareCompatible(software: SoftwareInstallationPair, osType: string, osVersion: string): boolean {
    if (!software || !software.installations) {
      return false;
    }

    if (osType.toLowerCase() === 'windows') {
      return this.isSoftwareCompatibleWindows(software, osVersion);
    }

    if (osType.toLowerCase() === 'linux') {
      return this.isSoftwareCompatibleLinux(software, osVersion);
    }

    const osConfig = software.installations[osType];
    if (osConfig && Object.prototype.hasOwnProperty.call(osConfig, osVersion)) {
      const versionConfig = osConfig[osVersion];
      return !!versionConfig && typeof versionConfig === 'object' && Object.keys(versionConfig).length > 0;
    }

    return false;
  }

  /**
   * Filters a software array based on OS compatibility.
   */
  filterByOs(software: SoftwareInstallationPair[], osType: string, osVersion: string): SoftwareInstallationPair[] {
    if (!software || !Array.isArray(software)) {
      return [];
    }

    const filtered = software.filter(sw => this.isSoftwareCompatible(sw, osType, osVersion));
    this.log(`Filter completed: ${filtered.length} of ${software.length} items for ${osType}:${osVersion}`);
    
    return filtered;
  }

  /**
   * Helper: Check Windows compatibility
   * Software shows only if exact version key exists in JSON
   */
  private isSoftwareCompatibleWindows(software: SoftwareInstallationPair, osVersion: string): boolean {
    const windowsConfig = software.installations?.['windows'];
    
    if (!windowsConfig) {
      return false;
    }

    if (Object.prototype.hasOwnProperty.call(windowsConfig, osVersion)) {
      const versionConfig = windowsConfig[osVersion];
      if (versionConfig && typeof versionConfig === 'object') {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper: Check Linux compatibility
   * Software shows only if version key exists and has scriptUrl
   */
  private isSoftwareCompatibleLinux(software: SoftwareInstallationPair, osVersion: string): boolean {
    const linuxConfig = software.installations?.['linux'];
    
    if (!linuxConfig) {
      return false;
    }

    if (!Object.prototype.hasOwnProperty.call(linuxConfig, osVersion)) {
      return false;
    }

    const versionConfig = linuxConfig[osVersion];
    
    if (!versionConfig || typeof versionConfig !== 'object') {
      return false;
    }

    return !!(versionConfig as any)?.scriptUrl;
  }

  /**
   * Dynamically extracts all available OS types from a list of software items.
   */
  getAvailableOsTypes(softwareList: SoftwareInstallationPair[]): string[] {
    if (!softwareList || !Array.isArray(softwareList)) {
      return [];
    }
    const osTypes = new Set<string>();
    for (const sw of softwareList) {
      if (sw.installations) {
        Object.keys(sw.installations).forEach(osType => osTypes.add(osType));
      }
    }
    return Array.from(osTypes);
  }

  /**
   * Dynamically extracts all available OS versions for a given OS type from a list of software items.
   */
  getAvailableOsVersions(softwareList: SoftwareInstallationPair[], osType: string): string[] {
    if (!softwareList || !Array.isArray(softwareList) || !osType) {
      return [];
    }
    const versions = new Set<string>();
    for (const sw of softwareList) {
      const osConfig = sw.installations?.[osType];
      if (osConfig) {
        Object.keys(osConfig)
          .filter(key => key !== 'base')
          .forEach(version => versions.add(version));
      }
    }
    return Array.from(versions);
  }
}